import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';

interface TreeNode {
    key: string;
    value?: any;
    type: 'object' | 'array' | 'primitive';
    diffType: 'same' | 'added' | 'removed' | 'modified';
    leftValue?: any;
    rightValue?: any;
    children?: TreeNode[];
    expanded: boolean;
    level: number;
}

@Component({
    selector: 'app-json-diff-dialog',
    templateUrl: './json-diff-dialog.component.html',
    styleUrls: ['./json-diff-dialog.component.scss']
})
export class JsonDiffDialogComponent {
    leftJson: string;
    rightJson: string;
    treeNodes: TreeNode[] = [];
    flattenedNodes: TreeNode[] = [];

    constructor(
        public dialogRef: MatDialogRef<JsonDiffDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { leftJson: any; rightJson: any }
    ) {
        console.log('JsonDiffDialog data:', data);
        this.leftJson = JSON.stringify(data.leftJson, null, 2);
        this.rightJson = JSON.stringify(data.rightJson, null, 2);
        this.buildTree();
    }

    buildTree(): void {
        this.treeNodes = this.compareAndBuildTree(this.data.leftJson, this.data.rightJson, 'root', 0);
        if (this.treeNodes.length > 0 && this.treeNodes[0].key === 'root') {
            this.treeNodes = this.treeNodes[0].children || [];
        }
        this.updateFlattenedNodes();
    }

    compareAndBuildTree(left: any, right: any, key: string, level: number): TreeNode[] {
        const leftType = this.getType(left);
        const rightType = this.getType(right);

        if (left === undefined || left === null) {
            return this.buildTreeFromValue(right, key, 'added', level, right, undefined);
        }

        if (right === undefined || right === null) {
            return this.buildTreeFromValue(left, key, 'removed', level, undefined, left);
        }

        if (leftType !== rightType) {
            const node: TreeNode = {
                key,
                type: 'primitive',
                diffType: 'modified',
                leftValue: left,
                rightValue: right,
                expanded: false,
                level
            };
            return [node];
        }

        if (leftType === 'object') {
            const leftKeys = Object.keys(left);
            const rightKeys = Object.keys(right);
            const allKeys = new Set([...leftKeys, ...rightKeys]);
            const children: TreeNode[] = [];

            for (const k of Array.from(allKeys).sort()) {
                const leftHasKey = leftKeys.includes(k);
                const rightHasKey = rightKeys.includes(k);

                if (!leftHasKey) {
                    children.push(...this.buildTreeFromValue(right[k], k, 'added', level + 1, right[k], undefined));
                } else if (!rightHasKey) {
                    children.push(...this.buildTreeFromValue(left[k], k, 'removed', level + 1, undefined, left[k]));
                } else {
                    const childNodes = this.compareAndBuildTree(left[k], right[k], k, level + 1);
                    children.push(...childNodes);
                }
            }

            // Parent is only different if it has direct children that are added/removed
            const hasDirectDiff = children.some(c => c.diffType === 'added' || c.diffType === 'removed');
            const diffType: 'same' | 'added' | 'removed' | 'modified' = hasDirectDiff ? 'modified' : 'same';

            const node: TreeNode = {
                key,
                type: 'object',
                diffType,
                leftValue: left,
                rightValue: right,
                children,
                expanded: true,
                level
            };
            return [node];
        } else if (leftType === 'array') {
            const maxLen = Math.max(left.length, right.length);
            const children: TreeNode[] = [];

            for (let i = 0; i < maxLen; i++) {
                if (i >= left.length) {
                    children.push(...this.buildTreeFromValue(right[i], `[${i}]`, 'added', level + 1, right[i], undefined));
                } else if (i >= right.length) {
                    children.push(...this.buildTreeFromValue(left[i], `[${i}]`, 'removed', level + 1, undefined, left[i]));
                } else {
                    const childNodes = this.compareAndBuildTree(left[i], right[i], `[${i}]`, level + 1);
                    children.push(...childNodes);
                }
            }

            // Parent is only different if it has direct children that are added/removed
            const hasDirectDiff = children.some(c => c.diffType === 'added' || c.diffType === 'removed');
            const diffType: 'same' | 'added' | 'removed' | 'modified' = hasDirectDiff ? 'modified' : 'same';

            const node: TreeNode = {
                key,
                type: 'array',
                diffType,
                leftValue: left,
                rightValue: right,
                children,
                expanded: true,
                level
            };
            return [node];
        } else {
            const diffType = JSON.stringify(left) === JSON.stringify(right) ? 'same' : 'modified';
            const node: TreeNode = {
                key,
                type: 'primitive',
                diffType,
                leftValue: left,
                rightValue: right,
                expanded: false,
                level
            };
            return [node];
        }
    }

    buildTreeFromValue(value: any, key: string, diffType: 'added' | 'removed', level: number, rightValue?: any, leftValue?: any): TreeNode[] {
        const valueType = this.getType(value);

        if (valueType === 'object') {
            const children: TreeNode[] = [];
            for (const k of Object.keys(value).sort()) {
                children.push(...this.buildTreeFromValue(value[k], k, diffType, level + 1, rightValue ? value[k] : undefined, leftValue ? value[k] : undefined));
            }
            const node: TreeNode = {
                key,
                type: 'object',
                diffType,
                leftValue,
                rightValue,
                children,
                expanded: true,
                level
            };
            return [node];
        } else if (valueType === 'array') {
            const children: TreeNode[] = [];
            for (let i = 0; i < value.length; i++) {
                children.push(...this.buildTreeFromValue(value[i], `[${i}]`, diffType, level + 1, rightValue ? value[i] : undefined, leftValue ? value[i] : undefined));
            }
            const node: TreeNode = {
                key,
                type: 'array',
                diffType,
                leftValue,
                rightValue,
                children,
                expanded: true,
                level
            };
            return [node];
        } else {
            const node: TreeNode = {
                key,
                type: 'primitive',
                diffType,
                value,
                leftValue,
                rightValue,
                expanded: false,
                level
            };
            return [node];
        }
    }

    getType(value: any): string {
        if (value === null) return 'null';
        if (Array.isArray(value)) return 'array';
        return typeof value;
    }

    updateFlattenedNodes(): void {
        this.flattenedNodes = [];
        this.flattenTree(this.treeNodes);
    }

    flattenTree(nodes: TreeNode[]): void {
        for (const node of nodes) {
            this.flattenedNodes.push(node);
            if (node.expanded && node.children) {
                this.flattenTree(node.children);
            }
        }
    }

    toggleNode(node: TreeNode): void {
        node.expanded = !node.expanded;
        this.updateFlattenedNodes();
    }

    getDisplayValue(node: TreeNode): string {
        if (node.type === 'object') return '{ }';
        if (node.type === 'array') return '[ ]';

        if (node.diffType === 'modified') {
            const leftVal = node.leftValue === undefined ? 'undefined' : JSON.stringify(node.leftValue);
            const rightVal = node.rightValue === undefined ? 'undefined' : JSON.stringify(node.rightValue);
            return `${leftVal} → ${rightVal}`;
        }

        if (node.diffType === 'removed') {
            const val = node.leftValue !== undefined ? node.leftValue : node.value;
            return val === undefined ? 'undefined' : JSON.stringify(val);
        }

        if (node.diffType === 'added') {
            const val = node.rightValue !== undefined ? node.rightValue : node.value;
            return val === undefined ? 'undefined' : JSON.stringify(val);
        }

        const val = node.leftValue !== undefined ? node.leftValue : (node.rightValue !== undefined ? node.rightValue : node.value);
        return val === undefined ? 'undefined' : JSON.stringify(val);
    }

    hasChildren(node: TreeNode): boolean {
        return node.children && node.children.length > 0;
    }

    hasDescendantChanges(node: TreeNode): boolean {
        if (!node.children || node.children.length === 0) {
            return false;
        }

        for (const child of node.children) {
            if (child.diffType !== 'same') {
                return true;
            }
            if (this.hasDescendantChanges(child)) {
                return true;
            }
        }

        return false;
    }

    getNodeDiffClass(node: TreeNode): string {
        // If node itself has changes, use its diffType
        if (node.diffType !== 'same') {
            return node.diffType;
        }

        // If node is collapsed and has descendant changes, show as modified
        if (!node.expanded && this.hasDescendantChanges(node)) {
            return 'modified';
        }

        return 'same';
    }

    onOk(): void {
        this.dialogRef.close(true);
    }

    onCancel(): void {
        this.dialogRef.close(false);
    }

    onClose(): void {
        this.dialogRef.close();
    }

    copyLeftJson(): void {
        navigator.clipboard.writeText(this.leftJson).then(() => {
            console.log('Left JSON copied to clipboard');
        }).catch(err => {
            console.error('Failed to copy: ', err);
        });
    }

    copyRightJson(): void {
        navigator.clipboard.writeText(this.rightJson).then(() => {
            console.log('Right JSON copied to clipboard');
        }).catch(err => {
            console.error('Failed to copy: ', err);
        });
    }
}